import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';

import TextInput from '../common/Inputs/TextInput';
import SelectInput from '../common/Inputs/SelectInput';
import Icon from '../common/Icon';
import { submitPreQualification } from '../../js/api/pre-qualification';
import { COUNTRIES, getCountryName } from '../../js/utils/countries';

const initialValues = {
  parent_name: '',
  email: '',
  phone: '',
  location: '',
  referral: '',
  child_name: '',
  child_email: '',
  dob: '',
  grade: '',
  applying_for_grade: '',
  current_school: '',
  school_location: '',
  school_type: '',
  nationality: '',
  nationality_other: '',
  'g-recaptcha-response': ''
};

function buildOptions(t, prefix, keys) {
  return [
    { label: t('selectPlaceholder'), value: '' },
    ...keys.map((k) => ({ label: t(`${prefix}.${k}`), value: k }))
  ];
}

export default function ApplyForm() {
  const { t, i18n } = useTranslation(['ApplyPage']);
  const [submitted, setSubmitted] = useState(false);
  const [refCode, setRefCode] = useState('');
  const recaptchaRef = useRef(null);

  const governorates = buildOptions(t, 'governorates', [
    'cairo', 'giza', 'alexandria', 'qalyubia', 'dakahlia', 'sharqia',
    'gharbia', 'monufia', 'beheira', 'kafrElSheikh', 'damietta', 'portSaid',
    'ismailia', 'suez', 'northSinai', 'southSinai', 'faiyum', 'beniSuef',
    'minya', 'asyut', 'sohag', 'qena', 'luxor', 'aswan', 'redSea',
    'newValley', 'matrouh', 'outsideEgypt'
  ]);

  const grades = buildOptions(t, 'grades', [
    'kg1', 'kg2', 'grade1', 'grade2', 'grade3', 'grade4', 'grade5', 'grade6',
    'grade7', 'grade8', 'grade9', 'grade10', 'grade11', 'grade12'
  ]);

  const schoolTypes = buildOptions(t, 'schoolTypes', ['public', 'private', 'international']);
  const nationalities = buildOptions(t, 'nationalities', ['egyptian', 'other']);
  const referrals = buildOptions(t, 'referrals', ['socialMedia', 'googleSearch', 'friendFamily', 'schoolEvent', 'other']);
  const countries = [
    { label: t('selectPlaceholder'), value: '' },
    ...COUNTRIES.map((c) => ({ label: c.name, value: c.code }))
  ];

  const validationSchema = Yup.object().shape({
    parent_name: Yup.string().trim().required(t('requiredField')),
    email: Yup.string().email(t('invalidEmail')).required(t('requiredField')),
    phone: Yup.string()
      .required(t('requiredField'))
      .test('valid-phone', t('invalidPhone'), (v) => (v || '').replace(/\D/g, '').length >= 8),
    location: Yup.string().required(t('requiredField')),
    referral: Yup.string().required(t('requiredField')),
    child_name: Yup.string().trim().required(t('requiredField')),
    dob: Yup.date()
      .max(new Date(), t('invalidDate'))
      .required(t('requiredField')),
    grade: Yup.string().required(t('requiredField')),
    applying_for_grade: Yup.string().required(t('requiredField')),
    current_school: Yup.string().trim().required(t('requiredField')),
    school_location: Yup.string().required(t('requiredField')),
    school_type: Yup.string().required(t('requiredField')),
    nationality: Yup.string().required(t('requiredField')),
    nationality_other: Yup.string().when('nationality', {
      is: 'other',
      then: (schema) => schema.trim().required(t('requiredField')),
      otherwise: (schema) => schema
    }),
    child_email: Yup.string().email(t('invalidEmail')).notRequired(),
    'g-recaptcha-response': Yup.string().required(t('recaptchaError'))
  });

  const handleSubmit = async (values, { resetForm, setFieldValue }) => {
    try {
      const finalNationality = values.nationality === 'other'
        ? (values.nationality_other || '').trim()
        : t('nationalities.egyptian');
      const res = await submitPreQualification({
        parent_name: values.parent_name,
        email: values.email,
        phone: values.phone,
        location: values.location,
        referral: values.referral,
        child_name: values.child_name,
        child_email: values.child_email || null,
        dob: values.dob,
        grade: values.grade,
        applying_for_grade: values.applying_for_grade,
        current_school: values.current_school,
        school_location: getCountryName(values.school_location),
        school_type: values.school_type,
        nationality: finalNationality,
        locale_used: i18n.language,
        'g-recaptcha-response': values['g-recaptcha-response']
      });
      setRefCode(res?.refCode || `MNR-${Math.floor(100000 + Math.random() * 900000)}`);
      setSubmitted(true);
      resetForm();
    } catch (err) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setFieldValue('g-recaptcha-response', '');
      const msg = err?.message || t('genericError');
      toast.error(msg);
    }
  };

  if (submitted) {
    return (
      <div className="apply-form__success">
        <div className="apply-form__success-icon">
          <Icon name="check" />
        </div>
        <h2 className="apply-form__success-title">{t('successTitle')}</h2>
        <p className="apply-form__success-text">{t('successMessage')}</p>
        <div className="apply-form__success-ref">
          <span>{t('referenceNumber')}</span>
          <strong>{refCode}</strong>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ errors, touched, isSubmitting, setFieldValue, values }) => (
        <Form className="apply-form" noValidate>
          <div className="apply-form__grid">
            <fieldset className="apply-form__column">
              <legend className="apply-form__legend">{t('parentSection')}</legend>

              <TextInput
                label={t('parentName')}
                type="text"
                name="parent_name"
                id="parent_name"
                placeholder={t('parentNamePlaceholder')}
                icon="profile-icon"
                required
                hasError={touched.parent_name && errors.parent_name}
                theme="dark">
                {errors.parent_name && touched.parent_name ? (
                  <span className="error-msg">{errors.parent_name}</span>
                ) : null}
              </TextInput>

              <TextInput
                label={t('email')}
                type="email"
                name="email"
                id="email"
                placeholder={t('emailPlaceholder')}
                icon="open-envelope"
                required
                hasError={touched.email && errors.email}
                theme="dark">
                {errors.email && touched.email ? (
                  <span className="error-msg">{errors.email}</span>
                ) : null}
              </TextInput>

              <TextInput
                label={t('phone')}
                type="tel"
                name="phone"
                id="phone"
                placeholder={t('phonePlaceholder')}
                icon="phone"
                required
                hasError={touched.phone && errors.phone}
                theme="dark">
                {errors.phone && touched.phone ? (
                  <span className="error-msg">{errors.phone}</span>
                ) : (
                  <span className="apply-form__hint">{t('phoneHint')}</span>
                )}
              </TextInput>

              <SelectInput
                label={t('location')}
                name="location"
                id="location"
                icon="info"
                required
                options={governorates}
                hasError={touched.location && errors.location}
                theme="dark">
                {errors.location && touched.location ? (
                  <span className="error-msg">{errors.location}</span>
                ) : null}
              </SelectInput>

              <SelectInput
                label={t('referral')}
                name="referral"
                id="referral"
                icon="question"
                required
                options={referrals}
                hasError={touched.referral && errors.referral}
                theme="dark">
                {errors.referral && touched.referral ? (
                  <span className="error-msg">{errors.referral}</span>
                ) : null}
              </SelectInput>
            </fieldset>

            <fieldset className="apply-form__column apply-form__column--child">
              <legend className="apply-form__legend">{t('childSection')}</legend>

              <TextInput
                label={t('childName')}
                type="text"
                name="child_name"
                id="child_name"
                placeholder={t('childNamePlaceholder')}
                icon="profile-icon"
                required
                hasError={touched.child_name && errors.child_name}
                theme="dark">
                {errors.child_name && touched.child_name ? (
                  <span className="error-msg">{errors.child_name}</span>
                ) : null}
              </TextInput>

              <TextInput
                label={t('childEmail')}
                type="email"
                name="child_email"
                id="child_email"
                placeholder={t('childEmailPlaceholder')}
                icon="open-envelope"
                hasError={touched.child_email && errors.child_email}
                theme="dark">
                {errors.child_email && touched.child_email ? (
                  <span className="error-msg">{errors.child_email}</span>
                ) : (
                  <span className="apply-form__hint">{t('childEmailHint')}</span>
                )}
              </TextInput>

              <TextInput
                label={t('dob')}
                type="date"
                name="dob"
                id="dob"
                icon="calendar-outline"
                required
                hasError={touched.dob && errors.dob}
                theme="dark">
                {errors.dob && touched.dob ? (
                  <span className="error-msg">{errors.dob}</span>
                ) : null}
              </TextInput>

              <SelectInput
                label={t('grade')}
                name="grade"
                id="grade"
                icon="grade"
                required
                options={grades}
                hasError={touched.grade && errors.grade}
                theme="dark">
                {errors.grade && touched.grade ? (
                  <span className="error-msg">{errors.grade}</span>
                ) : null}
              </SelectInput>

              <SelectInput
                label={t('applyingForGrade')}
                name="applying_for_grade"
                id="applying_for_grade"
                icon="grade"
                required
                options={grades}
                hasError={touched.applying_for_grade && errors.applying_for_grade}
                theme="dark">
                {errors.applying_for_grade && touched.applying_for_grade ? (
                  <span className="error-msg">{errors.applying_for_grade}</span>
                ) : null}
              </SelectInput>

              <TextInput
                label={t('currentSchool')}
                type="text"
                name="current_school"
                id="current_school"
                placeholder={t('currentSchoolPlaceholder')}
                icon="curriculum-icon"
                required
                hasError={touched.current_school && errors.current_school}
                theme="dark">
                {errors.current_school && touched.current_school ? (
                  <span className="error-msg">{errors.current_school}</span>
                ) : null}
              </TextInput>

              <SelectInput
                label={t('schoolLocation')}
                name="school_location"
                id="school_location"
                icon="info"
                required
                options={countries}
                hasError={touched.school_location && errors.school_location}
                theme="dark">
                {errors.school_location && touched.school_location ? (
                  <span className="error-msg">{errors.school_location}</span>
                ) : null}
              </SelectInput>

              <SelectInput
                label={t('schoolType')}
                name="school_type"
                id="school_type"
                icon="curriculum-icon"
                required
                options={schoolTypes}
                hasError={touched.school_type && errors.school_type}
                theme="dark">
                {errors.school_type && touched.school_type ? (
                  <span className="error-msg">{errors.school_type}</span>
                ) : null}
              </SelectInput>

              <SelectInput
                label={t('nationality')}
                name="nationality"
                id="nationality"
                icon="info"
                required
                options={nationalities}
                hasError={touched.nationality && errors.nationality}
                theme="dark">
                {errors.nationality && touched.nationality ? (
                  <span className="error-msg">{errors.nationality}</span>
                ) : null}
              </SelectInput>

              {values.nationality === 'other' ? (
                <TextInput
                  label={t('nationalityOther')}
                  type="text"
                  name="nationality_other"
                  id="nationality_other"
                  placeholder={t('nationalityOtherPlaceholder')}
                  icon="info"
                  required
                  hasError={touched.nationality_other && errors.nationality_other}
                  theme="dark">
                  {errors.nationality_other && touched.nationality_other ? (
                    <span className="error-msg">{errors.nationality_other}</span>
                  ) : null}
                </TextInput>
              ) : null}
            </fieldset>
          </div>

          <div className="apply-form__aid">
            <div className="apply-form__aid-icon" aria-hidden="true">★</div>
            <div>
              <p className="apply-form__aid-title">{t('financialAidTitle')}</p>
              <p className="apply-form__aid-text">{t('financialAidText')}</p>
            </div>
          </div>

          <div className="apply-form__recaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={'6LdSPigrAAAAAE8N8WaSf4Fw4EZ7Up3H6bWD2I63'}
              onChange={(token) => setFieldValue('g-recaptcha-response', token || '')}
              onExpired={() => setFieldValue('g-recaptcha-response', '')}
            />
            {errors['g-recaptcha-response'] && touched['g-recaptcha-response'] ? (
              <span className="error-msg">{errors['g-recaptcha-response']}</span>
            ) : null}
          </div>

          <div className="apply-form__actions">
            <button
              type="submit"
              className="button submit-button"
              disabled={isSubmitting}>
              {isSubmitting ? t('submitting') : t('submit')}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
